import { useRef, useState, useCallback } from 'react';

// Each entry: { type, ...payload }
// Types: 'file_edit', 'file_create', 'file_delete', 'file_rename', 'ai_generation'

export default function useEditorHistory(projectId) {
  const undoStack = useRef([]);
  const redoStack = useRef([]);
  // Trigger re-render when stacks change
  const [, setTick] = useState(0);
  const bump = () => setTick(t => t + 1);

  // Reset when project changes
  const lastProjectId = useRef(projectId);
  if (lastProjectId.current !== projectId) {
    lastProjectId.current = projectId;
    undoStack.current = [];
    redoStack.current = [];
  }

  const push = useCallback((entry) => {
    undoStack.current.push(entry);
    redoStack.current = [];
    bump();
  }, []);

  const clearHistory = useCallback(() => {
    undoStack.current = [];
    redoStack.current = [];
    bump();
  }, []);

  const canUndo = undoStack.current.length > 0;
  const canRedo = redoStack.current.length > 0;

  // Returns the entry to apply, or null
  const popUndo = useCallback(() => {
    if (!undoStack.current.length) return null;
    const entry = undoStack.current.pop();
    redoStack.current.push(entry);
    bump();
    return entry;
  }, []);

  const popRedo = useCallback(() => {
    if (!redoStack.current.length) return null;
    const entry = redoStack.current.pop();
    undoStack.current.push(entry);
    bump();
    return entry;
  }, []);

  return { push, clearHistory, canUndo, canRedo, popUndo, popRedo };
}
